import React from "react";
import { useNavigate } from "react-router-dom";
import { Button } from "@/components/ui/button";
import Navbar from "@/components/Navbar";
import AdvertBanner from "@/components/AdvertBanner";
import Footer from "@/components/Footer";
import { ArrowLeft } from "lucide-react";

const PrivacyPage = () => {
  const navigate = useNavigate();
  return (
    <div className="min-h-screen bg-[#FAFBFC]" data-testid="privacy-page">
      <Navbar />
      <div className="pt-28 pb-16 px-4">
        <AdvertBanner />
        <article className="max-w-3xl mx-auto">
          <Button variant="ghost" className="mb-6 text-gray-500" onClick={() => navigate(-1)}>
            <ArrowLeft className="h-4 w-4 mr-2" /> Back
          </Button>
          <h1 className="text-3xl font-bold text-gray-900 mb-2">Privacy Policy</h1>
          <p className="text-sm text-gray-500 mb-8">Last updated: April 2026</p>

          <div className="prose max-w-none text-gray-700 space-y-6">
            <section>
              <h2 className="text-xl font-bold text-gray-900 mt-8 mb-3">1. Information We Collect</h2>
              <p className="leading-relaxed">When you use Fixedraws, we may collect the following types of information:</p>
              <ul className="list-disc pl-6 space-y-2 mt-3">
                <li><strong className="text-gray-900">Phone Number:</strong> Provided when making a purchase to identify your transaction and enable communication regarding your order.</li>
                <li><strong className="text-gray-900">Country and Currency Preference:</strong> Selected during purchase to display appropriate pricing and payment methods.</li>
                <li><strong className="text-gray-900">Payment Information:</strong> Transaction references and payment method used. We do not store credit card or bank account details directly.</li>
                <li><strong className="text-gray-900">Support Ticket Data:</strong> Name, email, phone, and message content submitted through our support system.</li>
                <li><strong className="text-gray-900">Usage Data:</strong> Pages visited, time spent on the site, browser type, and device information collected automatically through standard web technologies.</li>
              </ul>
            </section>

            <section>
              <h2 className="text-xl font-bold text-gray-900 mt-8 mb-3">2. How We Use Your Information</h2>
              <p className="leading-relaxed">We use the information we collect for the following purposes:</p>
              <ul className="list-disc pl-6 space-y-2 mt-3">
                <li>To process and fulfill your prediction package purchases</li>
                <li>To generate and manage your one-time view codes</li>
                <li>To verify payments and prevent fraudulent transactions</li>
                <li>To respond to your support tickets and inquiries</li>
                <li>To improve our website, services, and user experience</li>
                <li>To send service-related notifications when necessary</li>
                <li>To comply with legal obligations and enforce our Terms of Service</li>
              </ul>
            </section>

            <section>
              <h2 className="text-xl font-bold text-gray-900 mt-8 mb-3">3. Data Storage and Security</h2>
              <p className="leading-relaxed">Your data is stored on secure servers with industry-standard encryption and access controls. We implement appropriate technical and organizational measures to protect your personal information against unauthorized access, alteration, disclosure, or destruction. However, no method of transmission over the Internet or electronic storage is 100% secure, and we cannot guarantee absolute security.</p>
            </section>

            <section>
              <h2 className="text-xl font-bold text-gray-900 mt-8 mb-3">4. Data Sharing</h2>
              <p className="leading-relaxed">We do not sell, trade, or rent your personal information to third parties. We may share your information only in the following circumstances:</p>
              <ul className="list-disc pl-6 space-y-2 mt-3">
                <li>With payment processors to complete your transactions</li>
                <li>When required by law, court order, or governmental authority</li>
                <li>To protect the rights, property, or safety of Fixedraws, our users, or others</li>
                <li>With your explicit consent</li>
              </ul>
            </section>

            <section>
              <h2 className="text-xl font-bold text-gray-900 mt-8 mb-3">5. Cookies and Tracking</h2>
              <p className="leading-relaxed">We use cookies and similar tracking technologies to maintain your session, remember your preferences (such as language and timezone settings), and improve your browsing experience. You can control cookie settings through your browser preferences. Disabling cookies may affect the functionality of certain features on our website.</p>
            </section>

            <section>
              <h2 className="text-xl font-bold text-gray-900 mt-8 mb-3">6. Data Retention</h2>
              <p className="leading-relaxed">We retain your personal data only for as long as necessary to fulfill the purposes outlined in this Privacy Policy, unless a longer retention period is required by law. Purchase records and transaction data are retained for a minimum of 12 months for audit and compliance purposes. Support ticket data is retained for 6 months after resolution.</p>
            </section>

            <section>
              <h2 className="text-xl font-bold text-gray-900 mt-8 mb-3">7. Your Rights</h2>
              <p className="leading-relaxed">Depending on your jurisdiction, you may have the right to:</p>
              <ul className="list-disc pl-6 space-y-2 mt-3">
                <li>Access the personal data we hold about you</li>
                <li>Request correction of inaccurate personal data</li>
                <li>Request deletion of your personal data</li>
                <li>Object to or restrict the processing of your data</li>
                <li>Data portability (receive your data in a structured format)</li>
              </ul>
              <p className="leading-relaxed mt-3">To exercise any of these rights, please contact us via text at +1 (208) 315-6308 or submit a support ticket.</p>
            </section>

            <section>
              <h2 className="text-xl font-bold text-gray-900 mt-8 mb-3">8. Children's Privacy</h2>
              <p className="leading-relaxed">Our Service is not directed to individuals under the age of 18. We do not knowingly collect personal information from children. If we become aware that we have collected data from a child without parental consent, we will take steps to delete that information promptly.</p>
            </section>

            <section>
              <h2 className="text-xl font-bold text-gray-900 mt-8 mb-3">9. Changes to This Policy</h2>
              <p className="leading-relaxed">We may update this Privacy Policy from time to time. We will notify you of any changes by posting the new Privacy Policy on this page and updating the "Last updated" date. Your continued use of the Service after any changes indicates your acceptance of the updated policy.</p>
            </section>

            <section>
              <h2 className="text-xl font-bold text-gray-900 mt-8 mb-3">10. Contact Us</h2>
              <p className="leading-relaxed">If you have any questions or concerns about this Privacy Policy or our data practices, please contact us via text at +1 (208) 315-6308 or submit a support ticket through our website.</p>
            </section>
          </div>
        </article>
      </div>
      <Footer />
    </div>
  );
};

export default PrivacyPage;
